import * as THREE from 'three';
import { TileCoordinate, CubeFace } from '../Types';
import { UrlBuilder } from './UrlBuilder';
import { ALL_CUBE_FACES } from './constants';

/**
 * Spatial index statistics
 */
export interface SpatialIndexStats {
  spatialAreas: number;
  totalTiles: number;
  tilesByLevel: Record<number, number>;
  tilesByFace: Record<string, number>;
  visibleTiles: number;
}

/**
 * Manages spatial indexing and hierarchical tile organization
 * - Guarantees at most ONE visible LOD per spatial area (prevents z-fighting flicker)
 * - Deterministic draw ordering + polygon offset per LOD
 * - Tracks multiple tiles/siblings per (spatialKey, level)
 * - Per-spatialKey generation to ignore stale tiles
 */
export class SpatialIndex {
  /** Registry of all tracked tiles by tileKey (not equivalent to mesh.visible) */
  private readonly trackedTiles = new Map<string, THREE.Mesh>();

  /** spatialKey -> level -> tileKey -> mesh */
  private readonly spatialTileMap = new Map<
    string,
    Map<number, Map<string, THREE.Mesh>>
  >();

  /** spatialKey -> set(levels) currently loading */
  private readonly loadingSpatialAreas = new Map<string, Set<number>>();

  /** spatialKey -> current generation token */
  private readonly spatialGeneration = new Map<string, number>();

  /** Faces that had L1 tiles removed (bookkeeping) */
  private readonly removedLevel1Faces = new Set<CubeFace>();

  /** Global default generation seed */
  private defaultGeneration = 0;

  // ---------------------------
  // Public API
  // ---------------------------

  /**
   * Begin a new plan (camera/LOD) for a spatial area.
   * All tiles requested under the previous generation will be considered stale.
   */
  beginNewPlanForSpatialArea(spatialKey: string): number {
    const next = (this.spatialGeneration.get(spatialKey) ?? this.defaultGeneration) + 1;
    this.spatialGeneration.set(spatialKey, next);
    return next;
  }

  /** Read current generation for a spatial area */
  getCurrentGeneration(spatialKey: string): number {
    return this.spatialGeneration.get(spatialKey) ?? this.defaultGeneration;
  }

  /** Whether a tile requested under `generation` should still be accepted */
  shouldAcceptTile(spatialKey: string, generation: number): boolean {
    return generation === this.getCurrentGeneration(spatialKey);
  }

  /**
   * Add tile mesh to spatial index.
   * - Ensures deterministic render order & polygon offset by LOD
   * - Ensures only the target LOD is visible per spatial area (no coplanar overlap)
   * - If `requestGeneration` is provided and stale, the tile is ignored
   */
  addTile(coordinate: TileCoordinate, mesh: THREE.Mesh, requestGeneration?: number): void {
    const tileKey = UrlBuilder.generateTileKey(coordinate);
    const spatialKey = UrlBuilder.generateSpatialKey(coordinate);
    const level = coordinate.level;

    // Drop stale tiles (generation mismatch)
    if (requestGeneration !== undefined && !this.shouldAcceptTile(spatialKey, requestGeneration)) {
      // Don't attach or flash a stale mesh
      return;
    }

    // Ensure containers
    const levelMap = this.getOrCreateLevelMap(spatialKey, level);

    // Track
    this.trackedTiles.set(tileKey, mesh);
    levelMap.set(tileKey, mesh);

    // Mesh metadata (prefer using userData instead of parsing tileKey elsewhere)
    mesh.userData = mesh.userData || {};
    mesh.userData.tileKey = tileKey;
    mesh.userData.isTile = true;
    mesh.userData.face = coordinate.face;
    mesh.userData.level = level;
    mesh.userData.x = coordinate.x;
    mesh.userData.y = coordinate.y;
    mesh.userData.spatialKey = spatialKey;
    mesh.userData.generation = this.getCurrentGeneration(spatialKey);

    // Deterministic draw ordering & polygon offset per LOD
    this.applyLODOrdering(mesh, level);
    
    // Do NOT force-hide other levels here.
    // Hiding is now coordinated by TransitionCoordinator based on coverage
    // to avoid black gaps during transitions.
  }

  /**
   * Remove tile by tileKey (does NOT dispose GPU resources).
   * Returns the mesh so caller can remove from scene & dispose.
   */
  removeTile(tileKey: string): THREE.Mesh | undefined {
    const mesh = this.trackedTiles.get(tileKey);
    if (!mesh) return undefined;

    this.trackedTiles.delete(tileKey);

    // Remove from spatial map
    const spatialKey = mesh.userData?.spatialKey as string | undefined
      ?? UrlBuilder.generateSpatialKeyFromTileKey(tileKey);

    const level = (mesh.userData?.level as number | undefined);
    if (spatialKey && (level ?? null) !== null) {
      const levelMap = this.spatialTileMap.get(spatialKey)?.get(level!);
      if (levelMap) {
        levelMap.delete(tileKey);
        if (levelMap.size === 0) {
          this.spatialTileMap.get(spatialKey)!.delete(level!);
          if (this.spatialTileMap.get(spatialKey)!.size === 0) {
            this.spatialTileMap.delete(spatialKey);
          }
        }
      }
    }

    return mesh;
  }

  /** Helper to dispose a mesh’s materials & geometries safely */
  disposeMeshResources(mesh: THREE.Mesh): void {
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const m of mats) {
      // @ts-ignore
      if (m?.map && typeof m.map.dispose === 'function') m.map.dispose();
      // @ts-ignore
      if (typeof m?.dispose === 'function') m.dispose();
    }
    mesh.geometry?.dispose?.();
  }

  /** Get tile mesh by tile key */
  getTile(tileKey: string): THREE.Mesh | undefined {
    return this.trackedTiles.get(tileKey);
  }

  /** Check if tile exists */
  hasTile(tileKey: string): boolean {
    return this.trackedTiles.has(tileKey);
  }

  /** Get all tiles in a spatial area: level -> (tileKey -> mesh) */
  getSpatialTiles(spatialKey: string): Map<number, Map<string, THREE.Mesh>> | undefined {
    return this.spatialTileMap.get(spatialKey);
  }

  /**
   * Check if a higher level tile is already loaded or being loaded for the same spatial area.
   * NOTE: same-level siblings are allowed (no >= short-circuit).
   */
  isHigherLevelTileLoaded(spatialKey: string, level: number): boolean {
    const levelContainer = this.spatialTileMap.get(spatialKey);
    if (levelContainer) {
      for (const existingLevel of levelContainer.keys()) {
        if (existingLevel > level) return true;
      }
    }
    const loading = this.loadingSpatialAreas.get(spatialKey);
    if (loading) {
      for (const l of loading) {
        if (l > level) return true;
      }
    }
    return false;
  }

  /** Mark spatial area as loading at specific level */
  markSpatialAreaLoading(spatialKey: string, level: number): void {
    let set = this.loadingSpatialAreas.get(spatialKey);
    if (!set) {
      set = new Set<number>();
      this.loadingSpatialAreas.set(spatialKey, set);
    }
    set.add(level);
  }

  /** Clear loading state for a specific level in a spatial area */
  clearSpatialAreaLoading(spatialKey: string, level: number): void {
    const set = this.loadingSpatialAreas.get(spatialKey);
    if (!set) return;
    set.delete(level);
    if (set.size === 0) this.loadingSpatialAreas.delete(spatialKey);
  }

  /** Get a shallow copy of tracked tiles */
  getVisibleTiles(): Map<string, THREE.Mesh> {
    return new Map(this.trackedTiles);
  }

  /** Get a shallow copy of spatial areas (top-level) */
  getSpatialAreas(): Map<string, Map<number, Map<string, THREE.Mesh>>> {
    return new Map(this.spatialTileMap);
  }

  /** Count tiles for a given LOD level */
  countTilesForLevel(level: number): number {
    let count = 0;
    for (const mesh of this.trackedTiles.values()) {
      if ((mesh.userData?.level as number) === level) count++;
    }
    return count;
  }

  /** Count how many unique cube faces have any tiles at a given level */
  getFaceCoverageForLevel(level: number): number {
    const faces = new Set<string>();
    for (const mesh of this.trackedTiles.values()) {
      const ud = mesh.userData || {};
      if (ud.level === level && ud.face != null) faces.add(String(ud.face));
    }
    return faces.size;
  }

  /** Count how many unique cube faces have any tiles loaded (at any level) */
  countFacesWithTiles(): number {
    const faces = new Set<string>();
    for (const mesh of this.trackedTiles.values()) {
      const face = mesh.userData?.face;
      if (face != null) faces.add(String(face));
    }
    return faces.size;
  }

  /** Get tiles for a specific face (any level) */
  getTilesForFace(face: string): Map<string, THREE.Mesh> {
    const out = new Map<string, THREE.Mesh>();
    for (const [k, mesh] of this.trackedTiles) {
      if (String(mesh.userData?.face) === face) out.set(k, mesh);
    }
    return out;
  }

  /** Get tiles for a specific face and level */
  getTilesForFaceAndLevel(face: string, level: number): Map<string, THREE.Mesh> {
    const out = new Map<string, THREE.Mesh>();
    for (const [k, mesh] of this.trackedTiles) {
      const ud = mesh.userData || {};
      if (String(ud.face) === face && ud.level === level) out.set(k, mesh);
    }
    return out;
  }

  /**
   * Hide all levels below `newLevel` in a spatial area AND ensure only `newLevel` remains visible.
   * This is safer than toggling booleans ad hoc during transitions.
   */
  hideLowerLevelTilesInSpatialArea(
    spatialKey: string,
    newLevel: number,
    opts: { requireVisibleTarget?: boolean } = { requireVisibleTarget: true }
  ): void {
    const levels = this.spatialTileMap.get(spatialKey);
    if (!levels) return;

    // NEW: Don’t hide parents if the new level isn’t visible yet
    if (opts.requireVisibleTarget !== false && !this.hasAnyVisibleAt(spatialKey, newLevel)) {
      return; // keep lower LODs visible → no black holes
    }

    for (const [lvl, tileMap] of levels) {
      if (lvl < newLevel) {
        for (const mesh of tileMap.values()) mesh.visible = false;
      }
    }
  }

  /**
   * Strong guarantee: exactly ONE LOD visible for the area (the `targetLevel`).
   * Use this when a new tile/LOD becomes ready.
   */
  showOnlyLevel(
    spatialKey: string,
    targetLevel: number,
    opts: { requireVisibleTarget?: boolean } = { requireVisibleTarget: true }
  ): void {
    const levels = this.spatialTileMap.get(spatialKey);
    if (!levels) return;

    // NEW: Don’t switch if target isn’t visible yet
    if (opts.requireVisibleTarget !== false && !this.hasAnyVisibleAt(spatialKey, targetLevel)) {
      return; // leave current state as-is
    }

    for (const [lvl, tileMap] of levels) {
      const vis = (lvl === targetLevel);
      for (const mesh of tileMap.values()) mesh.visible = vis;
    }
  }

  /** Get lower level tiles in spatial area */
  getLowerLevelTilesInSpatialArea(spatialKey: string, newLevel: number): THREE.Mesh[] {
    const levels = this.spatialTileMap.get(spatialKey);
    if (!levels) return [];
    const out: THREE.Mesh[] = [];
    for (const [lvl, tileMap] of levels) {
      if (lvl < newLevel) for (const mesh of tileMap.values()) out.push(mesh);
    }
    return out;
  }

  /** Mark face as having level 1 tiles removed */
  markLevel1FaceRemoved(face: CubeFace): void {
    this.removedLevel1Faces.add(face);
  }

  /** Check if level 1 tiles have been removed for face */
  isLevel1FaceRemoved(face: CubeFace): boolean {
    return this.removedLevel1Faces.has(face);
  }

  /** Clear level 1 face removal tracking */
  clearLevel1FaceRemoval(): void {
    this.removedLevel1Faces.clear();
  }

  /** Get statistics about the spatial index */
  getStats(): SpatialIndexStats {
    const tilesByLevel: Record<number, number> = {};
    const tilesByFace: Record<string, number> = {};

    for (const f of ALL_CUBE_FACES) tilesByFace[f] = 0;

    for (const mesh of this.trackedTiles.values()) {
      const ud = mesh.userData || {};
      const lvl = (ud.level as number) ?? -1;
      const face = String(ud.face ?? 'UNKNOWN');

      tilesByLevel[lvl] = (tilesByLevel[lvl] || 0) + 1;
      tilesByFace[face] = (tilesByFace[face] || 0) + 1;
    }

    return {
      spatialAreas: this.spatialTileMap.size,
      totalTiles: this.trackedTiles.size,
      tilesByLevel,
      tilesByFace,
      visibleTiles: this.trackedTiles.size,
    };
  }

  /** Debug: Get detailed tile counts by face -> level */
  debugGetFaceStats(): Map<string, Map<number, number>> {
    const faceStats = new Map<string, Map<number, number>>();
    for (const face of ALL_CUBE_FACES) faceStats.set(face, new Map());

    for (const [spatialKey, levelMap] of this.spatialTileMap) {
      const face = String(spatialKey.split('_')[0]);
      const stats = faceStats.get(face)!;
      for (const [level, tiles] of levelMap) {
        const prev = stats.get(level) || 0;
        stats.set(level, prev + tiles.size);
      }
    }
    return faceStats;
  }

  /** Clear all tiles and bookkeeping */
  clear(): void {
    this.trackedTiles.clear();
    this.spatialTileMap.clear();
    this.loadingSpatialAreas.clear();
    this.removedLevel1Faces.clear();
    this.spatialGeneration.clear();
  }

  /**
   * Validate spatial index integrity
   */
  validate(): { isValid: boolean; issues: string[] } {
    const issues: string[] = [];

    // A) Every tracked tile must exist in spatialTileMap under its (spatialKey, level, tileKey)
    for (const [tileKey, mesh] of this.trackedTiles) {
      const ud = mesh.userData || {};
      const spatialKey = ud.spatialKey as string | undefined
        ?? UrlBuilder.generateSpatialKeyFromTileKey(tileKey);
      const level = ud.level as number | undefined;

      if (!spatialKey || (level ?? null) === null) {
        issues.push(`Tile ${tileKey} missing spatialKey/level in userData`);
        continue;
      }

      const levelMap = this.spatialTileMap.get(spatialKey)?.get(level!);
      if (!levelMap || !levelMap.has(tileKey)) {
        issues.push(`Tracked tile ${tileKey} not found in spatial map at ${spatialKey} L${level}`);
      }

      if (ud.tileKey !== tileKey) {
        issues.push(`Mesh for ${tileKey} has mismatched userData.tileKey`);
      }
    }

    // B) Every spatial entry must be present in trackedTiles
    for (const [spatialKey, levels] of this.spatialTileMap) {
      for (const [level, tiles] of levels) {
        for (const [tileKey, mesh] of tiles) {
          if (!this.trackedTiles.has(tileKey)) {
            issues.push(`Spatial tile ${tileKey} at ${spatialKey} L${level} not found in trackedTiles`);
          }
        }
      }
    }

    return { isValid: issues.length === 0, issues };
  }

  // ---------------------------
  // Internals
  // ---------------------------

  private getOrCreateLevelMap(spatialKey: string, level: number): Map<string, THREE.Mesh> {
    let levels = this.spatialTileMap.get(spatialKey);
    if (!levels) {
      levels = new Map<number, Map<string, THREE.Mesh>>();
      this.spatialTileMap.set(spatialKey, levels);
    }
    let tileMap = levels.get(level);
    if (!tileMap) {
      tileMap = new Map<string, THREE.Mesh>();
      levels.set(level, tileMap);
    }
    return tileMap;
  }

  /** Apply render order & polygon offset per LOD to mitigate transient overlaps */
  private applyLODOrdering(mesh: THREE.Mesh, level: number): void {
    mesh.renderOrder = 1000 + level; // higher LOD draws later

    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const m of mats) {
      if (!m) continue;
      // @ts-ignore - three types are loose here by design
      m.depthTest = true;
      // @ts-ignore
      m.depthWrite = true;
      // @ts-ignore
      m.polygonOffset = true;
      // @ts-ignore
      m.polygonOffsetFactor = -1;     // pull slightly toward the camera
      // @ts-ignore
      m.polygonOffsetUnits = -level;  // separate LODs deterministically
    }

    // Optional extra safety: minuscule normal nudge per LOD (commented out)
    // const tiny = 1e-4 * level;
    // mesh.position.addScaledVector(mesh.getWorldDirection(new THREE.Vector3()), tiny);
  }
  private hasAnyVisibleAt(spatialKey: string, level: number): boolean {
    const levels = this.spatialTileMap.get(spatialKey);
    if (!levels) return false;
    const tileMap = levels.get(level);
    if (!tileMap || tileMap.size === 0) return false;
    for (const mesh of tileMap.values()) {
      if (mesh.visible) return true;
    }
    return false;
  }

}
